'use client';
import { useResizeObserver } from '#pplx/frontend/common/hooks/useResizeObserver.js';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
/**
 * Hook that returns whether the referenced element's content overflows its box horizontally or
 * vertically. It measures synchronously on mount and whenever the enabled flag changes, coalesces
 * other render-driven measurements to one per animation frame, and observes the element with a
 * ResizeObserver, so it stays accurate as the element resizes. An optional enabled flag (default true) suspends measurement and
 * observation; the hook reports false while disabled.
 *
 * @category hooks
 * @keywords
 *   - use
 *   - is
 *   - element
 *   - truncated
 *   - hooks
 *   - hook
 *   - overflow
 *   - truncate
 *   - truncation
 *   - clamp
 *   - ellipsis
 *   - clipped
 *   - scrollwidth
 *   - clientwidth
 *   - resizeobserver
 *   - enabled
 *   - ref
 *   - tooltip
 *   - toggle-tip
 * @whenToUse Use useIsElementTruncated when you need to know whether an element's content is
 *   visually clipped so you can conditionally reveal it. The canonical use is gating a Tooltip on
 *   truncated text — pass the text element's ref to the hook and disable the tooltip when it
 *   returns false. It coalesces remeasurement after React renders and observes the element with
 *   ResizeObserver, so the result stays current as content and layout change.
 * @guidelineDo
 *   - Pass a ref to the element whose content may overflow — typically a node with truncating CSS
 *     such as a TextPrimitive with truncate or a clamped block
 *   - Use the returned boolean to conditionally enable a Tooltip or ToggleTip that reveals the full
 *     text only when it is actually clipped
 *   - Keep the ref on the element that does the clipping, not on an outer wrapper, so
 *     scrollWidth/clientWidth and scrollHeight/clientHeight compare the right box
 *   - Pass enabled: false in the options argument to suspend measurement and resize observation
 *     while truncation detection is not needed — the hook reports false until re-enabled
 * @guidelineDont
 *   - Don't show a tooltip or expand affordance unconditionally — gate it on the hook so the
 *     affordance appears only when content is truncated
 *   - Don't rely on it to detect every overflow style; it reports overflow via scroll vs client
 *     size and works with standard CSS truncation/clamping, not custom JS-measured layouts
 *   - Don't rely on it for non-React DOM or content mutations that neither cause the consuming
 *     component to re-render nor resize the observed element — it remeasures after React renders
 *     and on observed element resizes, not on arbitrary external DOM changes
 * @accessibility
 *   - The hook is read-only and does not manage ARIA attributes, focus, or announce truncation to
 *     assistive technology
 *   - When you hide the full text behind a truncation-gated tooltip, keep the complete value
 *     reachable for assistive tech — for example by keeping it in the accessible name or an
 *     aria-label
 *   - Truncation is presentational; do not remove meaningful content from the DOM based solely on
 *     this hook's result
 * @relatedComponents
 *   - tooltip
 *   - toggle-tip
 */
export function useIsElementTruncated(ref, { enabled = true } = {}) {
    const [isElementTruncated, setIsElementTruncated] = useState(false);
    const previousEnabledRef = useRef(null);
    const animationFrameRef = useRef(null);
    const measure = useCallback(() => {
        if (!enabled) {
            setIsElementTruncated(false);
            return;
        }
        const element = ref.current;
        const nextIsElementTruncated = element !== null &&
            (element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight);
        setIsElementTruncated((previous) => previous === nextIsElementTruncated ? previous : nextIsElementTruncated);
    }, [enabled, ref]);
    const measureRef = useRef(measure);
    measureRef.current = measure;
    useLayoutEffect(() => {
        if (previousEnabledRef.current !== enabled) {
            previousEnabledRef.current = enabled;
            if (animationFrameRef.current !== null) {
                window.cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            // Mount and enabled transitions measure synchronously: disabling must report false before
            // paint per the documented contract, and enabling must not blank a truncation affordance for
            // a frame. Only same-configuration re-renders are deferred below.
            measure();
            return;
        }
        // While disabled, the transition above already reset the state to false and nothing can
        // change it until re-enabled, so there is nothing to re-measure.
        if (!enabled) {
            return;
        }
        if (animationFrameRef.current !== null) {
            return;
        }
        animationFrameRef.current = window.requestAnimationFrame(() => {
            animationFrameRef.current = null;
            measureRef.current();
        });
    });
    useLayoutEffect(() => () => {
        // Reset so a StrictMode effect replay behaves like a fresh mount and measures synchronously.
        previousEnabledRef.current = null;
        if (animationFrameRef.current !== null) {
            window.cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);
    useResizeObserver({ ref, onResize: measure, enabled });
    return isElementTruncated;
}
//# sourceMappingURL=useIsElementTruncated.js.map